import React, {
  useCallback, useMemo, useState, ReactElement,
} from 'react';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Button from '../../../components/button/button.component';
import ModalButtonBlock from '../../../components/modal-button-block/modal-button-block.component';
import LeaveConfirmModal from '../../../components/leave-confirm-modal/leave-confirm-modal.component';
import Loader from '../../../components/loader/loader.component';
import ModalLayout from '../../../components/modal-layout/modal-layout.component';
import RequiredMark from '../../../components/required-mark/required-mark.component';
import ErrorBlock from '../../../components/error-block/error-block.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import FieldSelect from '../../../components/field-select/field-select.component';
import FieldMultiSelect from '../../../components/field-multi-select/field-multi-select.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import { DEAL_PARTICIPANT_ACCESS_LEVEL_VIEW } from '../../../constants/entity/permissions';
import PersonDefaultImage from '../../../static/images/person-default-image.png';

const DealParticipantListModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isOpen,
    isInlineSaving,
    dealId,
    errorMessage,
    dealParticipantGroupList,
    accessLevelList,
    contactNotParticipantList,
    saveFormDeps,
    onChange,
    onSaveForm,
    onCloseModal,
    onContactNotParticipantSearch,
    onContactNotParticipantSearchClose,
  } = props;

  const [selectedContacts, setSelectedContacts]: [Contact[], Function] = useState([]);
  const [selectedGroupId, setSelectedGroupId]: [string, Function] = useState('');
  const [selectedAccessLevel, setSelectedAccessLevel]: [string, Function] = useState('');
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [allContactsLinkedToUsers, setAllContactsLinkedToUsers]: [boolean, Function] = useState(true);

  const dealParticipantAccessLevelViewPermissions: string[] = useMemo((): string[] => [
    DEAL_PARTICIPANT_ACCESS_LEVEL_VIEW,
  ], []);

  const menuItemDealParticipantGroupList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemLabelKey: 'name',
    optionList: dealParticipantGroupList,
  }), [dealParticipantGroupList]);

  const menuItemContactNotParticipantList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionItemExtraLabelKey: 'email',
    optionList: contactNotParticipantList,
    defaultImage: PersonDefaultImage,
    selectedItemList: selectedContacts,
  }), [contactNotParticipantList, selectedContacts]);

  const menuItemAccessLevelList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: allContactsLinkedToUsers ? accessLevelList : [],
  }), [accessLevelList, allContactsLinkedToUsers]);

  const resetForm: Function = useCallback((): void => {
    setSelectedContacts([]);
    setSelectedGroupId('');
    setSelectedAccessLevel('');
  }, []);

  const cancelChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    resetForm();

    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  const applyChangesAndCloseModal: MouseEventHandlerFunction = useCallback((): void => {
    if (onSaveForm && selectedContacts && selectedContacts.length && selectedGroupId) {
      const selectedContactIds: number[] = selectedContacts.map((contactItem: Contact): number => contactItem.id);

      onSaveForm(selectedContactIds, selectedGroupId, selectedAccessLevel || null, (): void => {
        resetForm();
      });
    }
  }, [selectedContacts, selectedGroupId, selectedAccessLevel, ...(saveFormDeps || [])]);

  const closeModalByClickAway: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(true);
  }, []);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);

    resetForm();

    if (onCloseModal) {
      onCloseModal();
    }
  }, []);

  const contactsChange: Function = useCallback((contactValues: Contact[]): void => {
    setSelectedContacts(contactValues);

    const selectedContactsLinkedToUsers: boolean = contactValues.reduce((
      result: boolean, contactValue: Contact,
    ): boolean => {
      const contact: Contact = contactNotParticipantList && !!contactNotParticipantList.length && contactValue.id
        ? contactNotParticipantList.find((contactItem: Contact): boolean => contactItem.id === contactValue.id) : null;

      return result && !!contact && !!contact.user;
    }, true);

    const allContactsLinkedToUsersValue: boolean = !!contactValues
      && !!contactValues.length && selectedContactsLinkedToUsers;
    setAllContactsLinkedToUsers(allContactsLinkedToUsersValue);

    if (!allContactsLinkedToUsersValue) {
      setSelectedAccessLevel('');
    }

    if (onChange) {
      onChange();
    }
  }, [contactNotParticipantList]);

  const groupChange: Function = useCallback((groupIdValue: number): void => {
    setSelectedGroupId(groupIdValue);

    if (onChange) {
      onChange();
    }
  }, []);

  const accessLevelChange: Function = useCallback((accessLevelValue: string): void => {
    setSelectedAccessLevel(accessLevelValue);

    if (onChange) {
      onChange();
    }
  }, []);

  return (
    <ModalLayout
      isOpen={isOpen}
      title="New Participants"
      onCloseModal={cancelChangesAndCloseModal}
      onCloseModalByClickAway={closeModalByClickAway}
    >
      <Card isContainer withNoPadding>
        <Table>
          <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
            <TableRow>
              <TableCell>
                Contacts
                <RequiredMark />
              </TableCell>
              <TableCell>
                <FieldMultiSelect
                  isPlain
                  isBordered
                  withImage
                  withSearch
                  isDisabled={contactNotParticipantList && !contactNotParticipantList.length}
                  nameKey="fullName"
                  optionList={contactNotParticipantList}
                  values={selectedContacts}
                  defaultImage={PersonDefaultImage}
                  searchOptions={{
                    dealId: dealId || null,
                  }}
                  onSearch={onContactNotParticipantSearch}
                  onClose={onContactNotParticipantSearchClose}
                  onValuesChange={contactsChange}
                >
                  {menuItemContactNotParticipantList}
                </FieldMultiSelect>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                Group
                <RequiredMark />
              </TableCell>
              <TableCell>
                <FieldSelect
                  isPlain
                  isBordered
                  withBlankElement
                  isDisabled={dealParticipantGroupList && !dealParticipantGroupList.length}
                  value={selectedGroupId}
                  onChange={(event: EventSelectChange, value: string): void => {
                    groupChange(value);
                  }}
                >
                  {menuItemDealParticipantGroupList}
                </FieldSelect>
              </TableCell>
            </TableRow>
            <CheckAccess permissions={dealParticipantAccessLevelViewPermissions}>
              <TableRow>
                <TableCell>
                  Access Level
                </TableCell>
                <TableCell>
                  <FieldSelect
                    isPlain
                    isBordered
                    withBlankElement
                    isDisabled={!allContactsLinkedToUsers || (accessLevelList && !accessLevelList.length)}
                    value={selectedAccessLevel}
                    onChange={(event: EventSelectChange, value: string): void => {
                      accessLevelChange(value);
                    }}
                  >
                    {menuItemAccessLevelList}
                  </FieldSelect>
                </TableCell>
              </TableRow>
            </CheckAccess>
          </TableBody>
        </Table>
      </Card>
      <ModalButtonBlock>
        <Button onClick={cancelChangesAndCloseModal}>
          Cancel
        </Button>
        <Button isActive isDisabled={isInlineSaving} onClick={applyChangesAndCloseModal}>
          Save
        </Button>
        {
          isInlineSaving && (
            <Loader isInline isModal isSmall />
          )
        }
      </ModalButtonBlock>
      {
        !!errorMessage && (
          <ErrorBlock errorMessage={errorMessage} />
        )
      }
      <LeaveConfirmModal
        isSubModal
        isOpen={isLeaveConfirmModalOpen}
        onConfirm={onLeaveConfirm}
        onCloseModal={closeLeaveConfirmModal}
      />
    </ModalLayout>
  );
});

export default DealParticipantListModal;
